import { useCallback, useState, useEffect } from 'react';
import useSession from './useSession';
import { getRuntimeEnv } from '@utils/runtimeEnv';

const STORAGE_API_BASE =
  getRuntimeEnv('REACT_APP_STORAGE_API_BASE') ||
  `${getRuntimeEnv('REACT_APP_SERVER_PROTOCOL')}://${getRuntimeEnv('REACT_APP_SERVER_HOST')}/storage`;


export const useApi = () => {
  const { accessToken } = useSession();

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (accessToken) {
      setIsReady(true);
    }
  }, [accessToken]);

  const callUploadApiMultiple = useCallback(
    async (files: File[], endpoint, params = {}) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      Object.entries(params).forEach(([key, value]) => {
        formData.set(key, value as string);
      });

      const res = await fetch(`${STORAGE_API_BASE}/${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Upload failed: ${res.status} - ${errorText}`);
      }

      return res.json();
    },
    [accessToken]
  );

  const fetchScreenshotDownloadUrl = useCallback(
    async (meterId: string, year: number, month: number) => {


      const url = `${STORAGE_API_BASE}/screenshots/${meterId}/${year}/${month}/url`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to get screenshot URL: ${res.status}`);
      }

      const response = await res.json();

      if (response.success && response.data?.url) {
        return response.data.url;
      } else {
        throw new Error('Failed to get screenshot URL');
      }
    },
    [accessToken]
  );

  const uploadSCADAFiles = useCallback(
    async (files: File[], params) => {
      return callUploadApiMultiple(files, 'screenshots/upload', params);
    },
    [callUploadApiMultiple]
  );

  const uploadMeterFiles = useCallback(
    async (files: File[]) => {
      return callUploadApiMultiple(files, 'meter-data/upload');
    },
    [callUploadApiMultiple]
  );

  const uploadNspLogo = useCallback(
    async (file: File, entryType: string) => {
      return callUploadApiMultiple([file], 'nsp-logos/upload', { entryType });
    },
    [callUploadApiMultiple]
  );

  const deleteNspLogo = useCallback(async (entryType: string) => {
    const res = await fetch(`${STORAGE_API_BASE}/nsp-logos/${entryType}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!res.ok) throw new Error('Delete failed');
    return res.json();
  }, [accessToken]);

  const checkPipelineStatusOnce = useCallback(
    async (runId: string) => {
      const baseUrl = STORAGE_API_BASE.startsWith('http')
        ? STORAGE_API_BASE
        : `${globalThis.location.origin}${STORAGE_API_BASE}`;

      const url = `${baseUrl}/meter-data/pipeline-status/${runId}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to check pipeline status: ${res.status}`);
      }

      // The endpoint wraps the status in { success, data } — unwrap so callers
      // get the flat { status, message, progress, runId } shape directly.
      const body = await res.json();
      return body.data;
    },
    [accessToken]
  );

  const checkScanStatus = useCallback(
    async (batchId: string) => {
      const baseUrl = STORAGE_API_BASE.startsWith('http')
        ? STORAGE_API_BASE
        : `${globalThis.location.origin}${STORAGE_API_BASE}`;

      const url = `${baseUrl}/meter-data/scan-status/${batchId}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to check malware scan status: ${res.status}`);
      }

      return res.json();
    },
    [accessToken]
  );

  const checkScadaScanStatus = useCallback(
    async (batchId: string) => {
      const baseUrl = STORAGE_API_BASE.startsWith('http')
        ? STORAGE_API_BASE
        : `${globalThis.location.origin}${STORAGE_API_BASE}`;

      const url = `${baseUrl}/screenshots/scan-status/${batchId}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to check malware scan status: ${res.status}`);
      }

      return res.json();
    },
    [accessToken]
  );

  const checkUploadLock = useCallback(async (): Promise<{ locked: boolean; status?: string | null; source?: string | null }> => {
    const baseUrl = STORAGE_API_BASE.startsWith('http')
      ? STORAGE_API_BASE
      : `${globalThis.location.origin}${STORAGE_API_BASE}`;

    const url = `${baseUrl}/meter-data/upload-lock`;

    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) return { locked: false };
      return res.json();
    } catch {
      return { locked: false };
    }
  }, [accessToken]);

  return {
    isReady,
    uploadSCADAFiles,
    uploadMeterFiles,
    fetchScreenshotDownloadUrl,
    checkPipelineStatusOnce,
    checkScanStatus,
    checkScadaScanStatus,
    checkUploadLock,
    uploadNspLogo,
    deleteNspLogo,
  };
};
